import { useEffect, useState, type ReactNode } from 'react'
import { useTheme } from '@/lib/theme'
import { contrastRatio, gradeContrast, type ContrastGrade } from '@/lib/contrast'
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  IconButton,
  Input,
  KeyValue,
  Select,
  Skeleton,
  Spinner,
  StatusDot,
  Switch,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
} from '@/components/ui'
import './styleguide.css'

/**
 * Lebender Styleguide.
 *
 * „Lebend" heisst: die Seite liest dieselben Tokens wie die Anwendung. Sie kann
 * gar nicht veralten. Wer in brand.css einen Seed austauscht, sieht hier sofort,
 * was das anrichtet — inklusive der Kontraste, die dabei kippen.
 */

function Section({
  id,
  title,
  lead,
  children,
}: {
  id: string
  title: string
  lead?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="sg-section" id={id}>
      <header className="sg-section__header">
        <h2 className="sg-section__title">{title}</h2>
        {lead && <p className="sg-section__lead">{lead}</p>}
      </header>
      {children}
    </section>
  )
}

function Swatch({ token, label }: { token: string; label: string }) {
  return (
    <div className="sg-swatch">
      <div className="sg-swatch__chip" style={{ background: `var(${token})` }} />
      <div className="sg-swatch__meta">
        <span className="sg-swatch__label">{label}</span>
        <code className="sg-swatch__token">{token}</code>
      </div>
    </div>
  )
}

const SURFACE_TOKENS = [
  ['--color-bg-sunken', 'Sunken'],
  ['--color-bg-canvas', 'Canvas'],
  ['--color-bg-surface', 'Surface'],
  ['--color-bg-surface-raised', 'Surface raised'],
  ['--color-bg-surface-overlay', 'Overlay'],
  ['--color-bg-inset', 'Inset'],
] as const

const TEXT_TOKENS = [
  ['--color-text-primary', 'Primär'],
  ['--color-text-secondary', 'Sekundär'],
  ['--color-text-tertiary', 'Tertiär'],
  ['--color-text-disabled', 'Deaktiviert'],
  ['--color-text-accent', 'Akzent'],
] as const

const TONE_TOKENS = [
  ['--color-success-fg', 'Erfolg'],
  ['--color-info-fg', 'Information'],
  ['--color-warning-fg', 'Warnung'],
  ['--color-danger-fg', 'Gefahr'],
] as const

const TYPE_SCALE = [
  ['--text-2xs', '11 px', 'Zeitstempel, Tabellen-Metadaten'],
  ['--text-xs', '12 px', 'Labels, Badges'],
  ['--text-sm', '13 px', 'Sekundärer UI-Text'],
  ['--text-base', '14 px', 'UI-Standard'],
  ['--text-md', '16 px', 'Fließtext in Dokumenten'],
  ['--text-lg', '18 px', 'Lead'],
  ['--text-xl', '22 px', 'H4'],
  ['--text-2xl', '28 px', 'H3'],
  ['--text-3xl', '36 px', 'H2'],
  ['--text-4xl', '48 px', 'H1'],
  ['--text-5xl', '64 px', 'Display'],
] as const

const SPACE_SCALE = [1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24] as const
const RADIUS_SCALE = ['xs', 'sm', 'md', 'lg', 'xl', 'full'] as const

/** Die Paarungen, die in der Anwendung tatsaechlich vorkommen. */
const CONTRAST_PAIRS: { fg: string; bg: string; label: string; large?: boolean }[] = [
  { fg: '--color-text-primary', bg: '--color-bg-canvas', label: 'Primärtext auf Canvas' },
  { fg: '--color-text-primary', bg: '--color-bg-surface', label: 'Primärtext auf Surface' },
  { fg: '--color-text-secondary', bg: '--color-bg-surface', label: 'Sekundärtext auf Surface' },
  { fg: '--color-text-tertiary', bg: '--color-bg-surface', label: 'Tertiärtext auf Surface' },
  { fg: '--color-text-accent', bg: '--color-bg-surface', label: 'Akzenttext auf Surface' },
  {
    fg: '--color-action-primary-fg',
    bg: '--color-action-primary-bg',
    label: 'Primärschaltfläche',
  },
  { fg: '--color-danger-fg', bg: '--color-bg-surface', label: 'Fehlertext auf Surface' },
  { fg: '--color-warning-fg', bg: '--color-bg-surface', label: 'Warntext auf Surface' },
  { fg: '--color-info-fg', bg: '--color-bg-surface', label: 'Infotext auf Surface' },
  { fg: '--color-success-fg', bg: '--color-bg-surface', label: 'Erfolgstext auf Surface' },
  {
    fg: '--color-border-default',
    bg: '--color-bg-surface',
    label: 'Rahmen auf Surface (Nicht-Text, 3:1)',
    large: true,
  },
]

const GRADE_TONE: Record<ContrastGrade, 'success' | 'info' | 'warning' | 'danger'> = {
  AAA: 'success',
  AA: 'success',
  'AA-large': 'warning',
  fail: 'danger',
}

function ContrastMatrix() {
  const { theme } = useTheme()
  const [rows, setRows] = useState<
    { label: string; ratio: number | null; grade: ContrastGrade | null }[]
  >([])

  useEffect(() => {
    // Nach dem Themenwechsel einen Frame warten, sonst misst man die alten Werte.
    const frame = requestAnimationFrame(() => {
      setRows(
        CONTRAST_PAIRS.map((pair) => {
          const ratio = contrastRatio(`var(${pair.fg})`, `var(${pair.bg})`)
          return {
            label: pair.label,
            ratio,
            grade: ratio === null ? null : gradeContrast(ratio, pair.large),
          }
        }),
      )
    })
    return () => cancelAnimationFrame(frame)
  }, [theme])

  const failures = rows.filter((row) => row.grade === 'fail').length

  return (
    <div className="stack">
      {failures > 0 ? (
        <Alert tone="danger" title={`${failures} Paarung(en) unter dem Grenzwert`}>
          Im Theme <strong>{theme}</strong>. Vor einem Marken-Swap sind das die
          Stellen, die nachgezogen werden müssen.
        </Alert>
      ) : (
        <Alert tone="success" title="Alle geprüften Paarungen bestehen">
          Gemessen im Theme <strong>{theme}</strong>, an den tatsächlich
          gerenderten Farben.
        </Alert>
      )}

      <Card flush>
        <Table>
          <thead>
            <tr>
              <Th>Paarung</Th>
              <Th numeric>Verhältnis</Th>
              <Th>Bewertung</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <Td>{row.label}</Td>
                <Td numeric>{row.ratio ? `${row.ratio.toFixed(2)}:1` : '—'}</Td>
                <Td>
                  {row.grade && (
                    <Badge tone={GRADE_TONE[row.grade]}>
                      {row.grade === 'AA-large' ? 'AA groß' : row.grade}
                    </Badge>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  )
}

export function Styleguide() {
  const { theme, toggle } = useTheme()

  return (
    <div className="styleguide stack-lg">
      <header className="sg-hero grid-backdrop">
        <p className="eyebrow eyebrow-accent">Design-System</p>
        <h1 className="display">Styleguide</h1>
        <p className="sg-hero__lead">
          Die Marke hängt an drei Seeds in <code>src/styles/brand.css</code>. Alles
          auf dieser Seite ist daraus abgeleitet und liest dieselben Tokens wie die
          Anwendung — dieser Styleguide kann nicht veralten.
        </p>
        <div className="cluster">
          <Button variant="secondary" onClick={toggle}>
            {theme === 'dark' ? 'Light ansehen' : 'Dark ansehen'}
          </Button>
          <Badge tone="neutral" outline>
            Theme: {theme}
          </Badge>
        </div>
      </header>

      <Alert tone="warning" title="Herkunft der Werte">
        Die Farb- und Schriftwerte sind aus dem öffentlichen NEURA-Web-Auftritt
        abgeleitet, nicht aus einem offiziellen Brand Manual übernommen. Sobald die
        offiziellen Werte vorliegen, werden ausschließlich die drei Seeds in{' '}
        <code>brand.css</code> ersetzt — das gesamte System zieht nach.
      </Alert>

      <Section
        id="farbe"
        title="Farbe"
        lead="Dark ist Default und entspricht der CI. Light bedient jeden Token ebenfalls — kein Token darf nur in einem Theme existieren."
      >
        <div className="stack">
          <h3 className="sg-subtitle">Flächen</h3>
          <div className="sg-swatch-grid">
            {SURFACE_TOKENS.map(([token, label]) => (
              <Swatch key={token} token={token} label={label} />
            ))}
          </div>

          <h3 className="sg-subtitle">Text</h3>
          <div className="sg-swatch-grid">
            {TEXT_TOKENS.map(([token, label]) => (
              <Swatch key={token} token={token} label={label} />
            ))}
          </div>

          <h3 className="sg-subtitle">Funktionsfarben</h3>
          <div className="sg-swatch-grid">
            {TONE_TOKENS.map(([token, label]) => (
              <Swatch key={token} token={token} label={label} />
            ))}
          </div>
        </div>
      </Section>

      <Section
        id="typografie"
        title="Typografie"
        lead="Archivo für Überschriften, Labels und Wortmarke — über die Breitenachse lässt sich die quadratische Anmutung von NEURA und 4NE1 annähern. Inter trägt die Oberfläche, JetBrains Mono die Messwerte. Selbst gehostet, kein Google-Fonts-CDN. Große Zeilen stehen im leichten Schnitt und in Satzschreibung; Versalien bleiben Wortmarke und Overlines vorbehalten."
      >
        <div className="stack">
          <div className="sg-type-samples">
            <div className="sg-type-sample">
              <p className="eyebrow">Wortmarke · Archivo, Breitenachse 120</p>
              <p className="wordmark" style={{ fontSize: 'var(--text-2xl)' }}>
                NEURA
              </p>
            </div>
            <div className="sg-type-sample">
              <p className="eyebrow">Display · Archivo Light, Satzschreibung</p>
              <p className="display">Die nächste Generation der Industriearbeit</p>
            </div>
            <div className="sg-type-sample">
              <p className="eyebrow">Fließtext · Inter</p>
              <p className="sg-body-sample">
                Die Achse eins erreicht ihre Nennlast bei 42 Nm. Eine Abweichung von
                mehr als drei Prozent führt zur Sperrung der Charge.
              </p>
            </div>
            <div className="sg-type-sample">
              <p className="eyebrow">Messwerte · JetBrains Mono</p>
              <p className="numeric sg-mono-sample">
                4NE-1-A07 · 42.318 Nm · 0.021 mm · 2026-08-26
              </p>
            </div>
          </div>

          <Card title="Größenskala" flush>
            <Table>
              <thead>
                <tr>
                  <Th>Token</Th>
                  <Th numeric>Größe</Th>
                  <Th>Einsatz</Th>
                  <Th>Muster</Th>
                </tr>
              </thead>
              <tbody>
                {TYPE_SCALE.map(([token, size, use]) => (
                  <tr key={token}>
                    <Td>
                      <code className="perm-chip">{token}</code>
                    </Td>
                    <Td numeric>{size}</Td>
                    <Td>{use}</Td>
                    <Td>
                      <span style={{ fontSize: `var(${token})` }}>NEURA</span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </div>
      </Section>

      <Section
        id="raster"
        title="Raster, Radien, Tiefe"
        lead="4px-Basis. Radien bleiben flach: Bedienelemente 4px, Flächen 10px. Technisch, nicht weich."
      >
        <div className="sg-columns">
          <Card title="Abstände">
            <div className="sg-space-list">
              {SPACE_SCALE.map((step) => (
                <div className="sg-space-row" key={step}>
                  <code className="sg-space-token">--space-{step}</code>
                  <div
                    className="sg-space-bar"
                    style={{ width: `var(--space-${step})` }}
                  />
                  <span className="numeric sg-space-value">{step * 4}px</span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Radien">
            <div className="sg-radius-grid">
              {RADIUS_SCALE.map((name) => (
                <div className="sg-radius" key={name}>
                  <div
                    className="sg-radius__box"
                    style={{ borderRadius: `var(--radius-${name})` }}
                  />
                  <code className="sg-swatch__token">--radius-{name}</code>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Tiefe">
            <div className="sg-shadow-grid">
              {['xs', 'sm', 'md', 'lg'].map((name) => (
                <div className="sg-shadow" key={name} style={{ boxShadow: `var(--shadow-${name})` }}>
                  <code className="sg-swatch__token">--shadow-{name}</code>
                </div>
              ))}
              <div className="sg-shadow" style={{ boxShadow: 'var(--shadow-glow)' }}>
                <code className="sg-swatch__token">--shadow-glow</code>
              </div>
            </div>
          </Card>
        </div>
      </Section>

      <Section
        id="komponenten"
        title="Komponenten"
        lead="Jede Komponente benutzt ausschließlich semantische Tokens. Kein roher Hex-Wert, kein --brand-* direkt."
      >
        <Tabs
          aria-label="Komponentengruppen"
          items={[
            {
              id: 'aktion',
              label: 'Aktion',
              content: (
                <div className="stack">
                  <div className="cluster">
                    <Button variant="primary">Freigeben</Button>
                    <Button variant="secondary">Bearbeiten</Button>
                    <Button variant="ghost">Abbrechen</Button>
                    <Button variant="danger">Löschen</Button>
                  </div>
                  <div className="cluster">
                    <Button size="sm" variant="primary">
                      Klein
                    </Button>
                    <Button size="md" variant="primary">
                      Mittel
                    </Button>
                    <Button size="lg" variant="primary">
                      Groß
                    </Button>
                  </div>
                  <div className="cluster">
                    <Button variant="primary" loading>
                      Wird gespeichert
                    </Button>
                    <Button variant="secondary" disabled>
                      Gesperrt
                    </Button>
                    <IconButton label="Einstellungen">
                      <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
                        <circle cx="8" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.4" />
                        <path
                          d="M8 1.5v1.8M8 12.7v1.8M14.5 8h-1.8M3.3 8H1.5"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                        />
                      </svg>
                    </IconButton>
                  </div>
                </div>
              ),
            },
            {
              id: 'eingabe',
              label: 'Eingabe',
              content: (
                <div className="sg-columns">
                  <div className="stack">
                    <FormField label="Sachnummer" hint="Format: 4NE-1-A07" required>
                      {(field) => <Input {...field} placeholder="4NE-1-…" />}
                    </FormField>
                    <FormField label="Nennmoment" error="Wert liegt außerhalb der Toleranz.">
                      {(field) => <Input {...field} inputMode="numeric" defaultValue="61.4" />}
                    </FormField>
                    <FormField label="Bereich">
                      {(field) => (
                        <Select {...field}>
                          <option>Mechanik</option>
                          <option>Elektronik</option>
                          <option>Robotik &amp; Regelung</option>
                        </Select>
                      )}
                    </FormField>
                  </div>
                  <div className="stack">
                    <FormField label="Anmerkung">
                      {(field) => <Textarea {...field} placeholder="Freitext…" />}
                    </FormField>
                    <Checkbox label="Prüfprotokoll liegt vor" defaultChecked />
                    <Checkbox label="Serienfreigabe erteilt" />
                    <Switch label="Änderungen nachverfolgen" defaultChecked />
                  </div>
                </div>
              ),
            },
            {
              id: 'status',
              label: 'Status',
              content: (
                <div className="stack">
                  <div className="cluster">
                    <Badge tone="accent">Freigegeben</Badge>
                    <Badge tone="info">In Prüfung</Badge>
                    <Badge tone="warning">Abweichung</Badge>
                    <Badge tone="danger">Gesperrt</Badge>
                    <Badge tone="neutral" outline>
                      Entwurf
                    </Badge>
                  </div>
                  <div className="cluster">
                    <StatusDot tone="success" label="Aktiv" />
                    <StatusDot tone="warning" label="Wartung fällig" />
                    <StatusDot tone="danger" label="Gesperrt" />
                  </div>
                  <Alert tone="info" title="Änderungsantrag ECR-2026-041">
                    Wartet seit drei Tagen auf fachliche Prüfung.
                  </Alert>
                  <Alert tone="danger" title="Kalibrierung abgelaufen">
                    Prüfmittel PM-118 darf nicht weiter verwendet werden.
                  </Alert>
                  <div className="cluster">
                    <Spinner />
                    <Skeleton width="180px" />
                    <Avatar name="Anna Berger" />
                    <Avatar name="Tom Klein" size="lg" />
                  </div>
                </div>
              ),
            },
            {
              id: 'daten',
              label: 'Daten',
              content: (
                <div className="stack">
                  <Card flush>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Bauteil</Th>
                          <Th>Bereich</Th>
                          <Th numeric>Nennmoment</Th>
                          <Th>Status</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['4NE-1-A01', 'Robotik', '42.318', 'success', 'Freigegeben'],
                          ['4NE-1-A07', 'Mechanik', '61.402', 'warning', 'Abweichung'],
                          ['4NE-1-B12', 'Elektronik', '8.940', 'danger', 'Gesperrt'],
                        ].map(([part, area, torque, tone, label]) => (
                          <tr key={part}>
                            <Td>
                              <code className="perm-chip">{part}</code>
                            </Td>
                            <Td>{area}</Td>
                            <Td numeric>{torque} Nm</Td>
                            <Td>
                              <StatusDot
                                tone={tone as 'success' | 'warning' | 'danger'}
                                label={label}
                              />
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card>

                  <Card title="Stammdaten">
                    <KeyValue
                      items={[
                        { label: 'Kennung', value: '4NE-1-A07', mono: true },
                        { label: 'Bereich', value: 'Mechanik' },
                        { label: 'Werkstoff', value: 'AlSi10Mg' },
                        { label: 'Toleranz', value: '± 0.021 mm', mono: true },
                      ]}
                    />
                  </Card>

                  <Card>
                    <EmptyState
                      title="Keine Änderungsanträge"
                      description="Sobald ein Antrag gestellt wird, erscheint er hier."
                      action={<Button variant="primary">Antrag stellen</Button>}
                    />
                  </Card>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section
        id="barrierefreiheit"
        title="Barrierefreiheit"
        lead="Die Kontraste werden hier zur Laufzeit an den tatsächlich gerenderten Farben gemessen — nicht aus einer gepflegten Tabelle vorgelesen. Theme umschalten und die Werte neu ablesen."
      >
        <ContrastMatrix />
      </Section>
    </div>
  )
}
